import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FigmaIcon from "./icons/FigmaIcon";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";

import PopupMultiSelectField from "./PopupMultiSelectField";
import { fetchRecruiterUsers } from "../utils/teamMembers";
import { resolvePublicAssetUrl } from "../utils/apiUrls";

const PURPLE = "#9C4FFF";
const BLUE = "#3451F4";
const BORDER = "#D0D5DD";
const MUTED = "#98A2B3";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 48,
    borderRadius: "12px",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: "#CBD5E1" },
    "&.Mui-focused fieldset": { borderColor: PURPLE, borderWidth: 2 },
  },
  "& .MuiInputBase-input": {
    fontSize: 13,
    fontFamily: "Be Vietnam Pro",
    color: "#475467",
  },
  "& .MuiInputBase-input::placeholder": {
    color: MUTED,
    opacity: 1,
  },
};

const chipSx = {
  height: 30,
  borderRadius: "999px",
  backgroundColor: "#EAF2FF",
  color: "#3451F4",
  fontWeight: 500,
  fontSize: 13,
  fontFamily: "Be Vietnam Pro",
  "& .MuiChip-label": {
    px: 1.35,
  },
  "& .MuiChip-deleteIcon": {
    color: "#3451F4",
    fontSize: 18,
  },
};

function trimText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function uniqText(values = []) {
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => trimText(value))
        .filter(Boolean)
    )
  );
}

function formatDocumentSize(size) {
  const numericValue = Number(size);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return "";
  if (numericValue < 1024) return `${numericValue} B`;
  if (numericValue < 1024 * 1024) return `${Math.round(numericValue / 1024)} KB`;
  return `${(numericValue / (1024 * 1024)).toFixed(1)} MB`;
}

function getDocumentKey(doc) {
  if (doc instanceof File) {
    return `${doc.name}-${doc.size}-${doc.lastModified}`;
  }

  return [
    doc?.id != null ? String(doc.id) : "",
    trimText(doc?.name || doc?.original_name || doc?.filename || "Document.pdf"),
    String(doc?.size || ""),
    String(doc?.lastModified || doc?.last_modified || ""),
    trimText(doc?.url || ""),
  ].join("|");
}

function getDocumentName(doc) {
  if (doc instanceof File) {
    return trimText(doc.name) || "Document.pdf";
  }

  return trimText(doc?.name || doc?.original_name || doc?.filename || "Document.pdf") || "Document.pdf";
}

function getDocumentMeta(doc, layout = "dropzone") {
  const sizeLabel = formatDocumentSize(doc?.size);
  const fallbackMeta = trimText(doc?.meta);

  if (layout === "inline") {
    if (sizeLabel) return `${sizeLabel} - 100% uploaded`;
    return fallbackMeta || "100% uploaded";
  }

  return fallbackMeta || sizeLabel || "";
}

function Label({ children, optional = false }) {
  return (
    <Typography data-popup-label
      sx={{
        mb: 0.75,
        fontSize: 13,
        fontWeight: 600,
        color: "#344054",
        fontFamily: "Be Vietnam Pro",
      }}
    >
      {children}
      {optional ? (
        <Box component="span" sx={{ color: MUTED, fontWeight: 500 }}>
          {" "}
          (optional)
        </Box>
      ) : null}
    </Typography>
  );
}

export default function ShareJobDialog({
  open,
  onClose,
  onSubmit,
  apiBase,
  resetKey,
  summaryItems = [],
  defaultTeamMembers = [],
  defaultEmailRecipients = [],
  emailSuggestions = [],
  defaultDocuments = [],
  notesDefault = "",
  title = "Share Job",
  sectionTitle = "Share with",
  submitLabel = "Share Job",
  showEmailField = true,
  headerIcon,
  documentLayout = "dropzone",
  recipientFields = [],
}) {
  const fileInputRef = useRef(null);
  const lastInitializedKeyRef = useRef("");
  const normalizedSummaryItems = useMemo(() => uniqText(summaryItems), [summaryItems]);
  const normalizedDefaultTeamMembers = useMemo(
    () => uniqText(defaultTeamMembers),
    [defaultTeamMembers]
  );
  const normalizedDefaultEmailRecipients = useMemo(
    () => uniqText(defaultEmailRecipients),
    [defaultEmailRecipients]
  );
  const normalizedEmailSuggestions = useMemo(
    () => uniqText([...defaultEmailRecipients, ...emailSuggestions]),
    [defaultEmailRecipients, emailSuggestions]
  );
  const normalizedDefaultDocuments = useMemo(
    () => (Array.isArray(defaultDocuments) ? defaultDocuments.filter(Boolean) : []),
    [defaultDocuments]
  );

  const [teamMemberOptions, setTeamMemberOptions] = useState([]);
  const [teamMemberRecords, setTeamMemberRecords] = useState([]);
  const renderTeamMemberAvatar = useCallback((name) => {
    const member = teamMemberRecords.find(row => row.name.toLowerCase() === String(name).trim().toLowerCase());
    const initials = String(name).trim().split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase();
    return <Avatar alt={name} src={resolvePublicAssetUrl(member?.avatar_url, { apiBase })}
      sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, bgcolor: "#EAF2FF", color: BLUE }}>
      {initials}
    </Avatar>;
  }, [apiBase, teamMemberRecords]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [teamMemberError, setTeamMemberError] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [emails, setEmails] = useState([]);
  const [notes, setNotes] = useState("");
  const [documents, setDocuments] = useState([]);
  const [recipientSelections, setRecipientSelections] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!open) return;

    const nextInitializedKey = String(resetKey ?? "__default__");
    if (lastInitializedKeyRef.current === nextInitializedKey) return;

    lastInitializedKeyRef.current = nextInitializedKey;
    setTeamMembers(normalizedDefaultTeamMembers);
    setEmails(normalizedDefaultEmailRecipients);
    setNotes(String(notesDefault || "").trim());
    setDocuments(normalizedDefaultDocuments);
    setSubmitError("");
    setRecipientSelections(
      Object.fromEntries(
        (Array.isArray(recipientFields) ? recipientFields : []).map((field) => [
          field.key,
          uniqText(field.defaultValue || []),
        ])
      )
    );
  }, [
    normalizedDefaultDocuments,
    notesDefault,
    normalizedDefaultEmailRecipients,
    normalizedDefaultTeamMembers,
    open,
    resetKey,
    recipientFields,
  ]);

  useEffect(() => {
    if (open) return;
    lastInitializedKeyRef.current = "";
  }, [open]);

  useEffect(() => {
    if (!open) return;

    let active = true;
    setLoadingTeamMembers(true);
    setTeamMemberError("");

    fetchRecruiterUsers({ apiBase })
      .then((rows) => {
        if (!active) return;
        setTeamMemberOptions(uniqText(rows.map((row) => row?.name)));
        setTeamMemberRecords(rows);
      })
      .catch((error) => {
        if (!active) return;
        console.error("Failed to load recruiter users for share dialog:", error);
        setTeamMemberOptions([]);
        setTeamMemberRecords([]);
        setTeamMemberError(error?.message || "Failed to load team members.");
      })
      .finally(() => {
        if (!active) return;
        setLoadingTeamMembers(false);
      });

    return () => {
      active = false;
    };
  }, [apiBase, open]);

  const handleSelectDocuments = useCallback((event) => {
    const nextFiles = Array.from(event.target?.files || []);
    if (!nextFiles.length) return;

    setDocuments((previous) => {
      const nextMap = new Map();
      [...previous, ...nextFiles].forEach((doc) => {
        const key = getDocumentKey(doc);
        if (!nextMap.has(key)) {
          nextMap.set(key, doc);
        }
      });
      return Array.from(nextMap.values());
    });

    event.target.value = "";
  }, []);

  const handleDeleteDocument = useCallback((targetFile) => {
    setDocuments((previous) =>
      previous.filter((file) => getDocumentKey(file) !== getDocumentKey(targetFile))
    );
  }, []);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      await onSubmit?.({
        teamMembers,
        emails,
        notes: String(notes || "").trim(),
        documents,
        recipients: recipientSelections,
      });
      onClose?.();
    } catch (error) {
      setSubmitError(error?.message || "Unable to share this profile.");
    } finally {
      setSubmitting(false);
    }
  }, [documents, emails, notes, onClose, onSubmit, recipientSelections, teamMembers]);

  const resolvedHeaderIcon = headerIcon || <FigmaIcon name="popup-share" />;
  const usesInlineDocuments = documentLayout === "inline";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          width: "min(690px, calc(100vw - 32px))",
          maxWidth: "690px",
          borderRadius: "22px",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.24)",
          m: 2,
        },
      }}
    >
      <Box sx={{ px: { xs: 2.25, sm: 4 }, py: { xs: 2.25, sm: 3.25 } }}>
        <Stack data-popup-header direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.3}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "999px",
                display: "grid",
                placeItems: "center",
                color: "#4F46E5",
                backgroundColor: "#F5F3FF",
              }}
            >
              {resolvedHeaderIcon}
            </Box>
            <Typography data-popup-title
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: "#101828",
                fontFamily: "Be Vietnam Pro",
              }}
            >
              {title}
            </Typography>
          </Stack>

          <IconButton data-popup-close
            onClick={handleClose}
            aria-label="Close"
            sx={{
              width: 32,
              height: 32,
              borderRadius: "999px",
              backgroundColor: PURPLE,
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: PURPLE,
                opacity: 0.92,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        {normalizedSummaryItems.length ? (
          <Box
            sx={{
              mt: 5,
              display: "flex",
              flexWrap: "wrap",
              gap: 0.8,
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: BLUE,
                fontFamily: "Be Vietnam Pro",
              }}
            >
              Info:
            </Typography>
            {normalizedSummaryItems.map((item, index) => (
              <React.Fragment key={`${item}-${index}`}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#475467",
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  {item}
                </Typography>
                {index < normalizedSummaryItems.length - 1 ? (
                  <Typography sx={{ fontSize: 13, color: "#D0D5DD" }}>|</Typography>
                ) : null}
              </React.Fragment>
            ))}
          </Box>
        ) : null}

        <Typography data-popup-section
          sx={{
            mt: 5,
            mb: 2,
            fontSize: 14,
            fontWeight: 700,
            color: BLUE,
            fontFamily: "Be Vietnam Pro",
          }}
        >
          {sectionTitle}
        </Typography>

        <Stack spacing={2.1}>
          <Box>
            <Label>Assign Team Member</Label>
            <PopupMultiSelectField
              placeholder="Search or assign team member"
              options={teamMemberOptions}
              renderAvatar={renderTeamMemberAvatar}
              value={teamMembers}
              onChange={setTeamMembers}
              loading={loadingTeamMembers}
              splitFreeSoloOnPipe={false}
              textFieldProps={{
                sx: fieldSx,
              }}
              chipSx={chipSx}
              autocompleteProps={{
                forcePopupIcon: true,
              }}
            />
            {teamMemberError ? (
              <Typography sx={{ mt: 0.85, fontSize: 12, color: "#F04438" }}>
                {teamMemberError}
              </Typography>
            ) : null}
          </Box>

          {showEmailField ? (
            <Box>
              <Label optional>By email</Label>
              <PopupMultiSelectField
                placeholder="name@company.com"
                options={normalizedEmailSuggestions}
                value={emails}
                onChange={setEmails}
                textFieldProps={{
                  sx: fieldSx,
                }}
                chipSx={chipSx}
                autocompleteProps={{
                  forcePopupIcon: true,
                }}
              />
            </Box>
          ) : null}

          {(Array.isArray(recipientFields) ? recipientFields : []).map((field) => (
            <Box key={field.key}>
              <Label optional={field.optional !== false}>{field.label}</Label>
              <PopupMultiSelectField
                placeholder={field.placeholder || `Choose ${String(field.label || "recipient").toLowerCase()}`}
                companyJobCounts={field.key === "companies"}
                options={uniqText(field.options || [])}
                value={recipientSelections[field.key] || []}
                onChange={(nextValue) =>
                  setRecipientSelections((current) => ({ ...current, [field.key]: nextValue }))
                }
                textFieldProps={{ sx: fieldSx }}
                chipSx={chipSx}
                autocompleteProps={{ forcePopupIcon: true }}
              />
            </Box>
          ))}

          <Box>
            <Label optional>Notes</Label>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Add a message for the team"
              sx={fieldSx}
            />
          </Box>

          <Box>
            <Label optional>Documents</Label>
            {usesInlineDocuments ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2.1, flexWrap: "wrap" }}>
                {documents.map((doc) => (
                  <Box
                    key={getDocumentKey(doc)}
                    sx={{
                      flex: "1 1 360px",
                      minWidth: "min(360px, 100%)",
                      maxWidth: "400px",
                      minHeight: 70,
                      borderRadius: "12px",
                      border: `1px solid ${PURPLE}`,
                      px: 1.8,
                      py: 1.45,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.15, minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "999px",
                          bgcolor: "#F5F3FF",
                          color: PURPLE,
                          display: "grid",
                          placeItems: "center",
                          flex: "0 0 auto",
                        }}
                      >
                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 18 }} />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            fontWeight: 500,
                            color: "#475467",
                            fontFamily: "Be Vietnam Pro",
                          }}
                          noWrap
                        >
                          {getDocumentName(doc)}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#667085",
                            mt: 0.1,
                            fontFamily: "Be Vietnam Pro",
                          }}
                        >
                          {getDocumentMeta(doc, "inline")}
                        </Typography>
                      </Box>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={() => handleDeleteDocument(doc)}
                      aria-label={`Remove ${getDocumentName(doc)}`}
                      sx={{
                        color: PURPLE,
                        p: 0.3,
                        flex: "0 0 auto",
                      }}
                    >
                      <IndeterminateCheckBoxOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                ))}

                <Typography
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: PURPLE,
                    cursor: "pointer",
                    fontFamily: "Be Vietnam Pro",
                    "&:hover": {
                      opacity: 0.86,
                    },
                  }}
                >
                  Add New
                </Typography>
              </Box>
            ) : (
              <Box
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                sx={{
                  minHeight: 86,
                  borderRadius: "14px",
                  border: "1.5px dashed #B8C3D9",
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                  transition: "border-color 120ms ease, background-color 120ms ease",
                  "&:hover": {
                    borderColor: PURPLE,
                    backgroundColor: "#FAF7FF",
                  },
                }}
              >
                <Stack spacing={0.8} alignItems="center">
                  <UploadFileOutlinedIcon sx={{ fontSize: 22, color: PURPLE }} />
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: PURPLE,
                      fontFamily: "Be Vietnam Pro",
                    }}
                  >
                    Add New
                  </Typography>
                </Stack>
              </Box>
            )}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              onChange={handleSelectDocuments}
            />

            {!usesInlineDocuments && documents.length ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.25 }}>
                {documents.map((file) => (
                  <Chip
                    key={getDocumentKey(file)}
                    label={getDocumentName(file)}
                    onDelete={() => handleDeleteDocument(file)}
                    sx={chipSx}
                  />
                ))}
              </Box>
            ) : null}
          </Box>
        </Stack>

        {submitError ? (
          <Typography sx={{ mt: 1.5, fontSize: 12.5, color: "#F04438" }}>
            {submitError}
          </Typography>
        ) : null}

        <Box data-popup-footer-inline sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
          <Button
            type="button"
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              minWidth: 132,
              height: 40,
              px: 2.5,
              borderRadius: "999px",
              textTransform: "none",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "Be Vietnam Pro",
              backgroundColor: PURPLE,
              border: `1px solid ${PURPLE}`,
              boxShadow: "none",
              transition:
                "background-color 140ms ease, color 140ms ease, border-color 140ms ease",
              "&:hover": {
                backgroundColor: "#FFFFFF",
                color: PURPLE,
                borderColor: PURPLE,
                boxShadow: "none",
              },
            }}
          >
            {submitting ? "Sharing…" : submitLabel}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
